import { useEffect, useMemo, useState } from 'react';
import { Typography, notification } from 'antd';

import { axiosGet, axiosPost } from '../../services/http.services';
import {
  API_ALLAIRPORT,
  API_DELETE_AIRPORT,
  API_UPDATE_AIRPORT,
} from '../../api/api';
import EditableGridTable from '../../customs/EditableGridTable';
import MapRender from '../../customs/MapRender';


const { Title } = Typography;


const mapAirportRow = (row = []) => ({
  code: row[0],
  name: row[1],
  group_name: row[2],
  status: row[3],
  live_date: row[4] ? new Date(row[4]).toISOString().slice(0, 10) : '',
  latitude: row[5],
  longitude: row[6],
});


const AirportManagementSection = ({ canEdit = false }) => {
  const [rows, setRows] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [hoveredAirportCode, setHoveredAirportCode] = useState('');

  const loadRows = async () => {
    try {
      const response = await axiosGet(API_ALLAIRPORT);
      setRows((response?.data || []).map(mapAirportRow));
    } catch (error) {
      notification.error({
        message: 'Unable to Load Airports',
        description: 'Airport configuration could not be loaded.',
      });
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  useEffect(() => {
    if (isEditMode) {
      setHoveredAirportCode('');
    }
  }, [isEditMode]);

  const mapRows = useMemo(
    () =>
      rows.map((row) => [
        row.code,
        row.name,
        row.group_name,
        row.status,
        row.live_date,
        row.latitude,
        row.longitude,
      ]),
    [rows]
  );

  const columns = [
    {
      key: 'code',
      title: 'Code',
      required: true,
      placeholder: 'BLR',
      width: 100,
    },
    {
      key: 'name',
      title: 'Name',
      required: true,
      placeholder: 'Bangalore',
      width: 180,
    },
    {
      key: 'group_name',
      title: 'Group Name',
      required: true,
      placeholder: 'AAI',
      width: 160,
    },
    {
      key: 'status',
      title: 'Status',
      required: true,
      placeholder: 'Live1',
      width: 120,
    },
    {
      key: 'live_date',
      title: 'Live Date',
      type: 'date',
      required: true,
      width: 150,
      renderReadonly: (value) => <span>{value ? new Date(value).toLocaleDateString() : ''}</span>,
    },
    {
      key: 'latitude',
      title: 'Lat',
      type: 'number',
      required: true,
      step: 0.000001,
      width: 120,
    },
    {
      key: 'longitude',
      title: 'Lng',
      type: 'number',
      required: true,
      step: 0.000001,
      width: 120,
    },
  ];

  const handleSaveRows = async (changedRows) => {
    for (const row of changedRows) {
      await axiosPost(API_UPDATE_AIRPORT, {
        Code: row.code,
        original_code: row.__original?.code || row.code,
        Name: row.name,
        'Group name': row.group_name,
        Status: row.status,
        'Live Date': row.live_date,
        lat: row.latitude,
        lng: row.longitude,
      });
    }
    await loadRows();
  };

  const handleDeleteRow = async (row) => {
    const airportCode = row.__original?.code || row.code;
    if (!airportCode) {
      return;
    }
    await axiosPost(API_DELETE_AIRPORT, { code: airportCode });
    await loadRows();
  };

  return (
    <div style={{ position: 'relative', marginTop: 25 }}>
      <Title level={4} style={{ textAlign: 'center' }}>
        Airport Names - IATA Codes
      </Title>

      <div
        style={{
          maxWidth: '100%',
          display: 'grid',
          gridTemplateColumns: isEditMode ? '1fr' : 'repeat(2, minmax(0, 1fr))',
          marginTop: 30,
          gap: 25,
        }}
      >
        <EditableGridTable
          data={rows}
          columns={columns}
          rowKey="code"
          canEdit={canEdit}
          onSaveRows={handleSaveRows}
          onDeleteRow={handleDeleteRow}
          getNewRow={() => ({
            code: '',
            name: '',
            group_name: '',
            status: '',
            live_date: '',
            latitude: 0,
            longitude: 0,
          })}
          cloneRow={(row) => ({
            ...row,
            code: row.code ? `${row.code}-COPY` : '',
          })}
          searchKeys={['code', 'name', 'group_name', 'status']}
          searchPlaceholder="Search airport"
          toolbarPosition="bottom"
        //   note="Airport setup here controls assignments, dashboard filters, and Data page scope."
          onEditModeChange={setIsEditMode}
          onRow={(row) => {
            if (isEditMode) {
              return {};
            }

            return {
              onMouseEnter: () => setHoveredAirportCode(row.code || ''),
              onMouseLeave: () =>
                setHoveredAirportCode((current) => (current === row.code ? '' : current)),
            };
          }}
        />

        {!isEditMode ? (
          <div style={{ position: 'sticky', top: 0, alignSelf: 'flex-start' }}>
            <MapRender isAdminPage AirportsLoc={mapRows} activeAirportCode={hoveredAirportCode} />
          </div>
        ) : null}
      </div>
    </div>
  );
};


export default AirportManagementSection;
